const DB_NAME = 'BdcThumbnailCache'
const STORE_NAME = 'thumbnails'
const DB_VERSION = 1

class IndexedDbService {
  private db: IDBDatabase | null = null

  constructor() {
    this.initDb()
  }

  private initDb(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.db) {
        console.log('[IndexedDbService] DB already initialized.')
        resolve()
        return
      }

      //console.log('[IndexedDbService] Initializing database...')
      const request = indexedDB.open(DB_NAME, DB_VERSION)

      request.onupgradeneeded = event => {
        console.log('[IndexedDbService] Upgrading database...')
        const db = (event.target as IDBOpenDBRequest).result
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME)
          console.log(`[IndexedDbService] Object store '${STORE_NAME}' created.`)
        }
      }

      request.onsuccess = event => {
        this.db = (event.target as IDBOpenDBRequest).result
        //console.log('[IndexedDbService] Database initialized successfully.')
        resolve()
      }

      request.onerror = event => {
        console.error(
          '[IndexedDbService] Database error:',
          (event.target as IDBOpenDBRequest).error
        )
        reject((event.target as IDBOpenDBRequest).error)
      }

      request.onblocked = () => {
        console.warn('[IndexedDbService] Database upgrade blocked. Please close other tabs.')
        reject(new Error('Database upgrade blocked'))
      }
    })
  }

  private async getDb(): Promise<IDBDatabase> {
    if (!this.db) {
      await this.initDb()
    }
    return this.db!
  }

  public async saveThumbnail(key: string, blob: Blob): Promise<void> {
    const db = await this.getDb()
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, 'readwrite')
      const store = transaction.objectStore(STORE_NAME)
      const request = store.put(blob, key)

      request.onsuccess = () => {
        // console.log(`[IndexedDbService] Thumbnail saved: ${key}`)
        resolve()
      }
      request.onerror = event => {
        console.error(
          `[IndexedDbService] Error saving thumbnail ${key}:`,
          (event.target as IDBRequest).error
        )
        reject((event.target as IDBRequest).error)
      }
    })
  }

  public async getThumbnail(key: string): Promise<Blob | null> {
    const db = await this.getDb()
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, 'readonly')
      const store = transaction.objectStore(STORE_NAME)
      const request = store.get(key)

      request.onsuccess = () => {
        // console.log(`[IndexedDbService] Thumbnail retrieved: ${key}`, request.result)
        resolve(request.result || null)
      }
      request.onerror = event => {
        console.error(
          `[IndexedDbService] Error getting thumbnail ${key}:`,
          (event.target as IDBRequest).error
        )
        reject((event.target as IDBRequest).error)
      }
    })
  }

  public async deleteThumbnail(key: string): Promise<void> {
    const db = await this.getDb()
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, 'readwrite')
      const store = transaction.objectStore(STORE_NAME)
      const request = store.delete(key)

      request.onsuccess = () => {
        // console.log(`[IndexedDbService] Thumbnail deleted: ${key}`)
        resolve()
      }
      request.onerror = event => {
        console.error(
          `[IndexedDbService] Error deleting thumbnail ${key}:`,
          (event.target as IDBRequest).error
        )
        reject((event.target as IDBRequest).error)
      }
    })
  }

  public async clearThumbnails(): Promise<void> {
    const db = await this.getDb()
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, 'readwrite')
      const store = transaction.objectStore(STORE_NAME)
      const request = store.clear()

      request.onsuccess = () => {
        console.log(`[IndexedDbService] Store '${STORE_NAME}' cleared.`)
        resolve()
      }
      request.onerror = event => {
        console.error(
          `[IndexedDbService] Error clearing store '${STORE_NAME}':`,
          (event.target as IDBRequest).error
        )
        reject((event.target as IDBRequest).error)
      }
    })
  }
}

export const indexedDbService = new IndexedDbService()
